import React, { useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import moment from "moment";
import Spinner1 from "../common/spinners";
import { truncateText } from "../../utils/utilities";
import PaymentHistoryModal from "../modals/PaymentHistoryModal";

const PaymentHistoryTable = ({ data, loading }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedData, setSelectedData] = useState();
  const itemsPerPage = 5;

  const filteredData = useMemo(
    () =>
      (Array.isArray(data) ? data : []).filter(
        (item) =>
          item?.service?.hmo?.hmoType?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.description?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.service?.item?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.patientRequestItem?.labTest?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.patientRequestItem?.drug?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.patientRequestItem?.drugGeneric?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
      ),
    [data, searchTerm]
  );

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const openDetailsModal = (item) => {
    setSelectedData(item);
    setShowSummaryModal(true);
  };

  return (
    <div className="w-100">
      <div className="data-panel-head">
        <div className="data-panel-copy">
          <h5>Payment history</h5>
          <p>Review payment records, related services, and billing status in a cleaner ledger.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search scheme, description, or service"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="data-table-shell">
        {loading ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="data-table-empty">
            <h6>No payment history found</h6>
            <p>Paid and unpaid items will appear here once transactions are available.</p>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="table data-table align-middle w-100">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Scheme</th>
                  <th>Amount</th>
                  <th>Service</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, index) => {
                  const serviceName =
                    item?.service?.item?.name ||
                    item?.patientRequestItem?.labTest?.name ||
                    item?.patientRequestItem?.drug?.name ||
                    item?.patientRequestItem?.drugGeneric?.name ||
                    (item?.bill_source === "ward" ? "Admission" : "---");

                  return (
                    <tr key={index} onClick={() => openDetailsModal(item)} style={{ cursor: "pointer" }}>
                      <td>
                        <div className="data-cell-title">{moment(item?.createdAt).format("Do MMM, YYYY")}</div>
                        <div className="data-cell-meta">{moment(item?.createdAt).format("dddd, h:mm A")}</div>
                      </td>
                      <td>
                        <div className="data-cell-title">{item?.service?.hmo?.name ?? item?.hmo?.name ?? "---"}</div>
                        <div className="data-cell-meta">
                          {item?.service?.hmo?.hmoType?.name ?? item?.hmo?.hmoType?.name ?? "---"}
                        </div>
                      </td>
                      <td>
                        <div className="data-cell-title">
                          {item?.service?.tariff?.toLocaleString("en-NG", {
                            style: "currency",
                            currency: "NGN",
                          }) ??
                            item?.amount?.toLocaleString("en-NG", {
                              style: "currency",
                              currency: "NGN",
                            })}
                        </div>
                        <div className="data-cell-meta">{item?.transaction_type ?? "---"}</div>
                      </td>
                      <td>
                        <div className="data-cell-title">{truncateText(`${serviceName}`, 28)}</div>
                        <div className="data-cell-meta">
                          {truncateText(`${item?.service?.item?.code ?? "N/A"}`, 15)}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`data-status-badge ${
                            item?.status === 1 ? "is-success" : "is-danger"
                          }`}
                        >
                          {item?.status === 1 ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredData.length > 0 && (
          <div className="data-pagination">
            <Button
              variant="light"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => page - 1)}
            >
              Previous
            </Button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="light"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <PaymentHistoryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        data={selectedData}
      />
    </div>
  );
};

export default PaymentHistoryTable;
